import JsonObject from '../../types/JsonObject';
import { useRef, useReducer, useCallback, Reducer } from 'react';
import useSafeDispatch from './useSafeDispatch';

interface State<T> {
  status: string;
  data: T;
  error: null;
}

const defaultInitialState = { status: 'idle', data: null, error: null };

export default function useAsync<T = object>(initialState: Partial<State<T>> = {}) {
  const initialStateRef = useRef<State<T>>({
    ...defaultInitialState,
    ...initialState,
  } as State<T>);
  const [{ status, data, error }, setState] = useReducer<Reducer<State<T>, any>>(
    (s, a) => ({ ...s, ...a }),
    initialStateRef.current,
  );

  const safeSetState = useSafeDispatch(setState);

  const setData = useCallback((data) => safeSetState({ data, status: 'resolved' }), [safeSetState]);
  const setError = useCallback((error) => safeSetState({ error, status: 'rejected' }), [safeSetState]);
  const reset = useCallback(() => safeSetState(initialStateRef.current), [safeSetState]);

  const run = useCallback(
    (promise) => {
      if (!promise || !promise.then) {
        throw new Error(
          `The argument passed to useAsync().run must be a promise. Maybe a function that's passed isn't returning anything?`,
        );
      }
      safeSetState({ status: 'pending' });
      return promise.then(
        (data: JsonObject) => {
          setData(data);
          return data;
        },
        (error: JsonObject) => {
          setError(error);
          return Promise.reject(error);
        },
      );
    },
    [safeSetState, setData, setError],
  );

  return {
    // using the same names that react-query uses for convenience
    isIdle: status === 'idle',
    isLoading: status === 'pending',
    isError: status === 'rejected',
    isSuccess: status === 'resolved',

    setData,
    setError,
    error,
    status,
    data,
    run,
    reset,
  };
}
